/**
 * FlagOverridesDialog
 *
 * Per-flag overrides surface opened from FeatureFlagsPage by clicking the
 * override-count chip. Two tabs:
 *   - Overrides: existing overrides table + inline "Add overrides" panel
 *     for bulk rollout to one or more tenants at once.
 *   - History: FEATURE_FLAG_UPDATE audit entries filtered client-side by
 *     details.flagKey === flagKey (design.md decision #4).
 *
 * Critical flags gate every write (per-row flip, per-row reset, and bulk
 * apply) behind CriticalFlagConfirmDialog. The dialog is re-keyed per
 * pending action so the typed-confirmation field always starts blank.
 */

import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Close as CloseIcon,
  RestartAlt as RestartAltIcon,
  SwapHoriz as SwapHorizIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { useSnackbar } from 'notistack';
import {
  useBulkSetFlagOverrides,
  useFeatureFlag,
  useFlagOverrides,
  usePlatformAuditLogs,
  useRemoveTenantOverride,
  useSetTenantOverride,
  useTenants,
} from '../../hooks';
import type { PlatformAuditLog, TenantFeatureOverride } from '../../types';
import { CriticalFlagConfirmDialog } from './CriticalFlagConfirmDialog';

type TabKey = 'overrides' | 'history';

type PendingAction =
  | { kind: 'flip'; tenantId: string; tenantName: string; nextValue: boolean }
  | { kind: 'reset'; tenantId: string; tenantName: string }
  | { kind: 'bulk'; tenantIds: string[]; value: boolean };

interface FlagOverridesDialogProps {
  open: boolean;
  flagKey: string;
  onClose: () => void;
}

interface TenantOption {
  id: string;
  name: string;
  slug: string;
}

const formatTransitionPart = (
  value: boolean | null | undefined
): string => {
  if (value === null || value === undefined) return 'default';
  return value ? 'true' : 'false';
};

const formatPerformedAt = (iso: string): string => {
  try {
    const date = new Date(iso);
    if (isNaN(date.getTime())) return 'Invalid date';
    return format(date, 'dd.MM.yyyy HH:mm');
  } catch {
    return 'Invalid date';
  }
};

const formatPerformer = (entry: PlatformAuditLog): string => {
  const { firstName, lastName, email } = entry.user;
  const name = `${firstName ?? ''} ${lastName ?? ''}`.trim();
  return name.length > 0 ? name : email;
};

const pluralizeTenants = (count: number): string =>
  count === 1 ? '1 tenant' : `${count} tenants`;

const describeBulk = (value: boolean, count: number): string =>
  `force ${value ? 'ON' : 'OFF'} for ${pluralizeTenants(count)}`;

const describeFlip = (nextValue: boolean, tenantName: string): string =>
  `force ${nextValue ? 'ON' : 'OFF'} for tenant ${tenantName}`;

const describeReset = (tenantName: string): string =>
  `reset to default for tenant ${tenantName}`;

export function FlagOverridesDialog({
  open,
  flagKey,
  onClose,
}: FlagOverridesDialogProps) {
  const { enqueueSnackbar } = useSnackbar();

  const flagQuery = useFeatureFlag(flagKey);
  const overridesQuery = useFlagOverrides(flagKey);
  const tenantsQuery = useTenants();

  const setOverride = useSetTenantOverride();
  const removeOverride = useRemoveTenantOverride();
  const bulkSetOverrides = useBulkSetFlagOverrides();

  const [currentTab, setCurrentTab] = useState<TabKey>('overrides');
  const [addPanelOpen, setAddPanelOpen] = useState(false);
  const [selectedTenants, setSelectedTenants] = useState<TenantOption[]>([]);
  const [bulkValue, setBulkValue] = useState<'on' | 'off'>('on');
  const [pendingAction, setPendingAction] = useState<PendingAction | null>(
    null
  );

  // Reset transient dialog state whenever the dialog is closed or the
  // targeted flag changes — a stale "Add overrides" form must not bleed
  // across opens.
  useEffect(() => {
    if (!open) {
      setCurrentTab('overrides');
      setAddPanelOpen(false);
      setSelectedTenants([]);
      setBulkValue('on');
      setPendingAction(null);
    }
  }, [open, flagKey]);

  const auditQuery = usePlatformAuditLogs(
    { action: 'FEATURE_FLAG_UPDATE', pageSize: 50 },
    { enabled: open && currentTab === 'history' }
  );

  const flag = flagQuery.data;
  const overrides = overridesQuery.data ?? [];

  const tenantOptions = useMemo<TenantOption[]>(() => {
    const tenants = tenantsQuery.data?.tenants ?? [];
    return tenants.map((t) => ({ id: t.id, name: t.name, slug: t.slug }));
  }, [tenantsQuery.data]);

  const tenantNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const opt of tenantOptions) map.set(opt.id, opt.name);
    return map;
  }, [tenantOptions]);

  const tenantsWithOverrides = useMemo(() => {
    return new Set((overridesQuery.data ?? []).map((o) => o.tenantId));
  }, [overridesQuery.data]);

  const availableTenantsForAdd = useMemo(() => {
    return tenantOptions.filter((t) => !tenantsWithOverrides.has(t.id));
  }, [tenantOptions, tenantsWithOverrides]);

  const auditEntries = useMemo(() => {
    const results = auditQuery.data?.results ?? [];
    return results.filter((entry) => {
      if (!entry.details || typeof entry.details !== 'object') return false;
      return (entry.details as Record<string, unknown>).flagKey === flagKey;
    });
  }, [auditQuery.data, flagKey]);

  const isMutating =
    setOverride.isPending ||
    removeOverride.isPending ||
    bulkSetOverrides.isPending;

  const handleClose = () => {
    if (isMutating) return;
    onClose();
  };

  // ---- per-row actions ----------------------------------------------------

  const dispatchFlip = async (
    tenantId: string,
    tenantName: string,
    nextValue: boolean
  ) => {
    try {
      await setOverride.mutateAsync({ key: flagKey, tenantId, value: nextValue });
      enqueueSnackbar(
        `${flagKey} forced ${nextValue ? 'ON' : 'OFF'} for ${tenantName}`,
        { variant: 'success' }
      );
    } catch {
      enqueueSnackbar(`Failed to update override for ${tenantName}`, {
        variant: 'error',
      });
    }
  };

  const dispatchReset = async (tenantId: string, tenantName: string) => {
    try {
      await removeOverride.mutateAsync({ key: flagKey, tenantId });
      enqueueSnackbar(`Removed override for ${tenantName}`, {
        variant: 'success',
      });
    } catch {
      enqueueSnackbar(`Failed to reset override for ${tenantName}`, {
        variant: 'error',
      });
    }
  };

  const handleFlipClick = (override: TenantFeatureOverride) => {
    const tenantName =
      override.tenant?.name ??
      tenantNameById.get(override.tenantId) ??
      override.tenantId;
    const nextValue = !override.value;
    if (flag?.isCritical) {
      setPendingAction({
        kind: 'flip',
        tenantId: override.tenantId,
        tenantName,
        nextValue,
      });
      return;
    }
    void dispatchFlip(override.tenantId, tenantName, nextValue);
  };

  const handleResetClick = (override: TenantFeatureOverride) => {
    const tenantName =
      override.tenant?.name ??
      tenantNameById.get(override.tenantId) ??
      override.tenantId;
    if (flag?.isCritical) {
      setPendingAction({
        kind: 'reset',
        tenantId: override.tenantId,
        tenantName,
      });
      return;
    }
    void dispatchReset(override.tenantId, tenantName);
  };

  // ---- bulk add -----------------------------------------------------------

  const dispatchBulk = async (tenantIds: string[], value: boolean) => {
    try {
      const result = await bulkSetOverrides.mutateAsync({
        key: flagKey,
        tenantIds,
        value,
      });

      const errorCount = result.errors.length;
      if (errorCount === 0) {
        enqueueSnackbar(
          `Applied ${flagKey} = ${value ? 'ON' : 'OFF'} to ${pluralizeTenants(result.applied)}`,
          { variant: 'success' }
        );
      } else {
        const head = result.errors
          .slice(0, 3)
          .map((e) => {
            const name = tenantNameById.get(e.tenantId) ?? e.tenantId;
            return `${name}: ${e.error}`;
          })
          .join('; ');
        const tail =
          errorCount > 3 ? ` (+${errorCount - 3} more)` : '';
        enqueueSnackbar(
          `Applied to ${pluralizeTenants(result.applied)}, ${errorCount} failed — ${head}${tail}`,
          { variant: 'warning' }
        );
      }

      setAddPanelOpen(false);
      setSelectedTenants([]);
      setBulkValue('on');
    } catch {
      enqueueSnackbar(`Failed to apply bulk override for ${flagKey}`, {
        variant: 'error',
      });
    }
  };

  const handleApplyBulk = () => {
    if (selectedTenants.length === 0) return;
    const tenantIds = selectedTenants.map((t) => t.id);
    const value = bulkValue === 'on';
    if (flag?.isCritical) {
      setPendingAction({ kind: 'bulk', tenantIds, value });
      return;
    }
    void dispatchBulk(tenantIds, value);
  };

  const handleCancelAddPanel = () => {
    setAddPanelOpen(false);
    setSelectedTenants([]);
    setBulkValue('on');
  };

  // ---- critical confirmation ---------------------------------------------

  const handleConfirmCritical = async () => {
    if (!pendingAction) return;
    const action = pendingAction;
    setPendingAction(null);
    if (action.kind === 'flip') {
      await dispatchFlip(action.tenantId, action.tenantName, action.nextValue);
    } else if (action.kind === 'reset') {
      await dispatchReset(action.tenantId, action.tenantName);
    } else {
      await dispatchBulk(action.tenantIds, action.value);
    }
  };

  const handleCancelCritical = () => {
    setPendingAction(null);
  };

  const criticalActionDescription = pendingAction
    ? pendingAction.kind === 'bulk'
      ? describeBulk(pendingAction.value, pendingAction.tenantIds.length)
      : pendingAction.kind === 'flip'
        ? describeFlip(pendingAction.nextValue, pendingAction.tenantName)
        : describeReset(pendingAction.tenantName)
    : '';

  const criticalDialogKey = pendingAction
    ? pendingAction.kind === 'bulk'
      ? `bulk:${pendingAction.value}:${pendingAction.tenantIds.length}`
      : pendingAction.kind === 'flip'
        ? `flip:${pendingAction.tenantId}:${pendingAction.nextValue}`
        : `reset:${pendingAction.tenantId}`
    : 'idle';

  // ---- render -------------------------------------------------------------

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        aria-labelledby="flag-overrides-dialog-title"
      >
        <DialogTitle
          id="flag-overrides-dialog-title"
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            pr: 6,
          }}
        >
          <Typography
            component="span"
            sx={{ fontFamily: 'monospace', fontWeight: 600 }}
          >
            {flagKey}
          </Typography>
          {flag?.isCritical && (
            <Chip label="CRITICAL" color="warning" size="small" />
          )}
          <Typography component="span" sx={{ ml: 1 }}>
            — Tenant overrides
          </Typography>
          <IconButton
            aria-label="Close"
            onClick={handleClose}
            sx={{ position: 'absolute', right: 8, top: 8 }}
            disabled={isMutating}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <Tabs
          value={currentTab}
          onChange={(_, value: TabKey) => setCurrentTab(value)}
          sx={{ borderBottom: 1, borderColor: 'divider', px: 3 }}
        >
          <Tab label="Overrides" value="overrides" />
          <Tab label="History" value="history" />
        </Tabs>

        <DialogContent>
          {currentTab === 'overrides' && (
            <Stack spacing={2} sx={{ pt: 1 }}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: 2,
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  Default value:{' '}
                  <strong>{flag?.defaultValue ? 'ON' : 'OFF'}</strong>. Tenants
                  listed here override the default.
                </Typography>
                {!addPanelOpen && (
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => setAddPanelOpen(true)}
                    disabled={isMutating}
                  >
                    Add overrides
                  </Button>
                )}
              </Box>

              {addPanelOpen && (
                <Box
                  sx={{
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 1,
                    p: 2,
                  }}
                >
                  <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
                    Add overrides for one or more tenants
                  </Typography>
                  <Stack spacing={2}>
                    <Autocomplete
                      multiple
                      options={availableTenantsForAdd}
                      value={selectedTenants}
                      loading={tenantsQuery.isLoading}
                      onChange={(_, value) => setSelectedTenants(value)}
                      getOptionLabel={(option) => option.name}
                      isOptionEqualToValue={(a, b) => a.id === b.id}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Tenants"
                          placeholder={
                            availableTenantsForAdd.length === 0
                              ? 'All tenants already have an override'
                              : 'Select tenants…'
                          }
                          size="small"
                        />
                      )}
                    />

                    <FormControl component="fieldset">
                      <RadioGroup
                        row
                        value={bulkValue}
                        onChange={(e) =>
                          setBulkValue(e.target.value as 'on' | 'off')
                        }
                      >
                        <FormControlLabel
                          value="on"
                          control={<Radio size="small" />}
                          label="Force ON"
                        />
                        <FormControlLabel
                          value="off"
                          control={<Radio size="small" />}
                          label="Force OFF"
                        />
                      </RadioGroup>
                    </FormControl>

                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      <Button
                        onClick={handleCancelAddPanel}
                        disabled={isMutating}
                      >
                        Cancel
                      </Button>
                      <Button
                        variant="contained"
                        onClick={handleApplyBulk}
                        disabled={
                          selectedTenants.length === 0 || isMutating
                        }
                      >
                        Apply
                      </Button>
                    </Stack>
                  </Stack>
                </Box>
              )}

              {overridesQuery.isLoading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress size={24} />
                </Box>
              )}

              {overridesQuery.isError && (
                <Alert severity="error">
                  Failed to load tenant overrides for this flag.
                </Alert>
              )}

              {!overridesQuery.isLoading &&
                !overridesQuery.isError &&
                overrides.length === 0 && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ py: 4, textAlign: 'center' }}
                  >
                    No tenant overrides for this flag yet
                  </Typography>
                )}

              {!overridesQuery.isLoading &&
                !overridesQuery.isError &&
                overrides.length > 0 && (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Tenant</TableCell>
                          <TableCell>Override</TableCell>
                          <TableCell align="right">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {overrides.map((override) => {
                          const name =
                            override.tenant?.name ??
                            tenantNameById.get(override.tenantId) ??
                            override.tenantId;
                          const nextValueLabel = override.value
                            ? 'OFF'
                            : 'ON';
                          return (
                            <TableRow key={override.id}>
                              <TableCell>{name}</TableCell>
                              <TableCell>
                                <Chip
                                  label={
                                    override.value ? 'Force ON' : 'Force OFF'
                                  }
                                  size="small"
                                  color={override.value ? 'success' : 'default'}
                                  variant="outlined"
                                />
                              </TableCell>
                              <TableCell align="right">
                                <Tooltip
                                  title={`Switch to Force ${nextValueLabel}`}
                                >
                                  <span>
                                    <IconButton
                                      size="small"
                                      onClick={() => handleFlipClick(override)}
                                      disabled={isMutating}
                                    >
                                      <SwapHorizIcon fontSize="small" />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                                <Tooltip title="Reset to default">
                                  <span>
                                    <IconButton
                                      size="small"
                                      onClick={() => handleResetClick(override)}
                                      disabled={isMutating}
                                    >
                                      <RestartAltIcon fontSize="small" />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
            </Stack>
          )}

          {currentTab === 'history' && (
            <Stack spacing={2} sx={{ pt: 1 }}>
              {auditQuery.isLoading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress size={24} />
                </Box>
              )}

              {auditQuery.isError && (
                <Alert severity="error">
                  Failed to load audit history.
                </Alert>
              )}

              {!auditQuery.isLoading &&
                !auditQuery.isError &&
                auditEntries.length === 0 && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ py: 4, textAlign: 'center' }}
                  >
                    No history yet for this flag
                  </Typography>
                )}

              {!auditQuery.isLoading &&
                !auditQuery.isError &&
                auditEntries.length > 0 && (
                  <Box
                    sx={{
                      maxHeight: 400,
                      overflowY: 'auto',
                      border: 1,
                      borderColor: 'divider',
                      borderRadius: 1,
                    }}
                  >
                    {auditEntries.map((entry, idx) => {
                      const details =
                        (entry.details as Record<string, unknown>) ?? {};
                      const previous = formatTransitionPart(
                        details.previousValue as
                          | boolean
                          | null
                          | undefined
                      );
                      const next = formatTransitionPart(
                        details.newValue as boolean | null | undefined
                      );
                      const tenantId = details.tenantId as string | undefined;
                      const tenantLabel = tenantId
                        ? (tenantNameById.get(tenantId) ?? tenantId)
                        : 'unknown tenant';
                      return (
                        <Box key={entry.id}>
                          {idx > 0 && <Divider />}
                          <Box sx={{ px: 2, py: 1.5 }}>
                            <Stack
                              direction="row"
                              spacing={1}
                              alignItems="center"
                              sx={{ flexWrap: 'wrap' }}
                            >
                              <Typography
                                variant="body2"
                                fontWeight={500}
                              >
                                {tenantLabel}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                by {formatPerformer(entry)}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {formatPerformedAt(entry.performedAt)}
                              </Typography>
                            </Stack>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ fontFamily: 'monospace', mt: 0.5 }}
                            >
                              {previous} → {next}
                            </Typography>
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>
                )}
            </Stack>
          )}
        </DialogContent>
      </Dialog>

      <CriticalFlagConfirmDialog
        key={criticalDialogKey}
        open={!!pendingAction}
        flagKey={flagKey}
        action={criticalActionDescription}
        onConfirm={handleConfirmCritical}
        onCancel={handleCancelCritical}
      />
    </>
  );
}
